import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import * as _ from 'lodash';
import {Observable, OperatorFunction} from 'rxjs';
import {ConfigurationService} from '../../../../entry-routes/configuration/configuration.service';
import {Aggregate} from '../../../models/aggregate';
import {AggregateOptions} from '../../../models/aggregate-request-params';
import {CdxDocument} from '../../../models/cdx-document';
import {Entity} from '../../../models/Entity';
import {EntityDetails} from '../../../models/entity-details';
import {EsPage} from '../../../models/es-page';
import {InternalRoutes} from '../../../models/internal-routes';
import {ObjectType} from '../../../models/ObjectType';
import {SearchItem} from '../../../models/search-item';
import {Url} from '../../../models/url';
import {ThesaurusService} from '../../../services/thesaurus/thesaurus.service';
import {CurrentContextService} from '../../current-context/service/current-context.service';
import {EntityFilterDatas, EntityFilterState} from '../../entity-search/entity-filter/reducer/entity-filter.reducer';
import {EntitySearchResultAction} from '../../entity-search/entity-search-result/action/entity-search-result.action';
import {
  selectEntityFilterDatas,
  selectEntityFilterFeature,
  selectEntitySearchItemDatas,
  selectEntitySearchResultDatas,
  selectEntitySearchResultFeature
} from '../../entity-search/entity-search-selectors';
import {GedCfgFieldService} from '../../field/service/ged/ged-cfg-field.service';
import {AbstractFilterService} from '../../filter/service/abstract-filter.service';
import {EntityFilterService} from '../../filter/service/entity-filter.service';
import {SearchResultPaginationService} from '../../search-result-pagination/service/search-result-pagination.service';
import {AbstractSearchResultService} from './abstract-search-result.service';
import {EntitySearchResultState} from '../../entity-search/entity-search-result/reducer/entity-search-result.reducer';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { AuthenticationService } from '../../../../entry-routes/authentication/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class EntitySearchResultService extends AbstractSearchResultService {

  entitySearchItems$ = this.store.select(selectEntitySearchItemDatas);
  entityFilterDatas$ = this.store.select(selectEntityFilterDatas);
  entitySearchListResult$ = this.store.select(selectEntitySearchResultDatas);

  private entitySearchResultState: EntitySearchResultState;
  private entityFilterState: EntityFilterState;

  private filterService: AbstractFilterService;

  constructor(
    http: HttpClient,
    fieldService: GedCfgFieldService,
    thesaurusService: ThesaurusService,
    entityFilterService: EntityFilterService,
    private entitySearchResultAction: EntitySearchResultAction,
    private configAction: ConfigurationService,
    private currentContextService: CurrentContextService,
    protected searchResultPaginationService: SearchResultPaginationService,
    store: Store,
    authenticationService: AuthenticationService
  ) {
    super(
      http,
      fieldService,
      thesaurusService,
      store,
      authenticationService
    );
    this.thesaurusService.setEntitySearchResultService(this);
    this.filterService = entityFilterService;

    this.store.select(selectEntitySearchResultFeature)
      .pipe(takeUntilDestroyed())
      .subscribe(entitySearchResultState => this.entitySearchResultState = entitySearchResultState);

    this.store.select(selectEntityFilterFeature)
      .pipe(takeUntilDestroyed())
      .subscribe(entityFilterState => this.entityFilterState = entityFilterState);

  }

  public getObjectType(): ObjectType {
    return ObjectType.ENTITY;
  }

  public getServiceType(): InternalRoutes {
    return InternalRoutes.ENTITIES;
  }

  public getSearchItem$(): Observable<SearchItem[]> {
    return this.entitySearchItems$;
  }

  public getFilterDatas$(): Observable<EntityFilterDatas> {
    return this.entityFilterDatas$;
  }

  public getSearchListResult$(): Observable<EsPage<Entity>> {
    return this.entitySearchListResult$;
  }

  public getSearchBaseUrl(): string {
    return Url.getProtectedApiBaseUrl(this.configAction) + Url.LINKS + this.currentContextService.getCurrentEntityType();
  }

  public getAggBaseUrl(): string {
    return Url.getProtectedApiBaseUrl(this.configAction) + Url.LINKS + this.currentContextService.getCurrentEntityType() + '/' + Url.AGGS;
  }

  public getEmptyEspage(): EsPage<Entity> {
    return new EsPage<Entity>(0, [], 0, 0, [], false);
  }

  public searchDrafts(pageNumber, keepLastAgg): void {
    console.log('entity-search-result-servcice searchDrafts() pageNumber:'+pageNumber+' keepLastAgg:'+keepLastAgg)
    return;
  }

  public search(): void {
    this.filterService.removeAllFilters();
    this.entitySearchResultAction.loadEntitiesStarted();
    try {
      super._search().subscribe((page: EsPage<Entity>) => {
          // load unknown fields
          this.cfgFieldService.loadFieldsFromPath(...page.aggs.map((agg: Aggregate) => agg.path));
          // send result to store
          this.entitySearchResultAction.loadEntitiesSucceeded(page as EsPage<Entity>);
        },
        (error: HttpErrorResponse) => {
          this.entitySearchResultAction.loadEntitiesFailed(error);

          // si plantage cela peut être du au tri, on le retire du store pour pouvoir relancer la recherche sans
          this.searchResultPaginationService.reinitSearchResultPagination();
        }
      );
    } catch (error) {
      this.entitySearchResultAction.loadEntitiesFailed(error);
    }
  }

  public setNewPageToStore(page: EsPage<Entity>): void {
    this.filterService.removeAllFilters();
    this.cfgFieldService.loadFieldsFromPath(...page.aggs.map((agg: Aggregate) => agg.path));
    // send result to store
    this.entitySearchResultAction.loadEntitiesSucceeded(page as EsPage<Entity>);
  }

  public filter(keepLastAgg = false): void {
    this.entitySearchResultAction.loadEntitiesStarted();
    try {
      super._filter()
        .subscribe((page: EsPage<CdxDocument | Entity>) => {
            // this.fieldService.loadFieldsFromPath(...page.aggs.map((agg: Aggregate) => agg.path));
            if (!!this.entityFilterState.datas.lastAgg && keepLastAgg) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (aggregate.path === this.entityFilterState.datas.lastAgg.path) {
                  return this.entityFilterState.datas.lastAgg;
                }
                return aggregate;
              });
            }
            // send result to store
            this.entitySearchResultAction.loadEntitiesSucceeded(page as EsPage<Entity>);
          }, (error: HttpErrorResponse) => {
            this.entitySearchResultAction.loadEntitiesFailed(error);
          }
        );
    } catch (error) {
      this.entitySearchResultAction.loadEntitiesFailed(error);
    }
  }

  public gotoPage(pageNum: number): void {
    this.entitySearchResultAction.updateEntitiesStarted();
    try {
      super._gotoPage(pageNum)
        .subscribe((page: EsPage<Entity>) => {
            this.cfgFieldService.loadFieldsFromPath(...page.aggs.map((agg: Aggregate) => agg.path));
            // load unknown fields
            if (!!this.entityFilterState && !!this.entityFilterState.datas && !!this.entityFilterState.datas.lastAgg) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (aggregate.path === this.entityFilterState.datas.lastAgg.path) {
                  return this.entityFilterState.datas.lastAgg;
                }
                return aggregate;
              });
            }
            this.entitySearchResultAction.updateEntitiesSucceeded(page as EsPage<Entity>);
          }, (error: HttpErrorResponse) => {
            this.entitySearchResultAction.updateEntitiesFailed(error);
          }
        );
    } catch (error) {
      this.entitySearchResultAction.updateEntitiesFailed(error);
    }
  }

  public loadAggregate(path: string, opts: AggregateOptions, checkPath = false): void {
    this.entitySearchResultAction.loadAggregateStart();
    try {
      super._loadAggregate(path, opts, checkPath)
        .subscribe((agg: Aggregate) => {
            // this.fieldService.loadFieldsFromPath(path);
            this.entitySearchResultAction.loadAggregateSucceeded(agg);
          },
          (error: HttpErrorResponse) => {
            this.entitySearchResultAction.loadAggregateFailed(error);
          });
    } catch (error) {
      this.entitySearchResultAction.loadAggregateFailed(error);
    }
  }

  public workspaceParams(): OperatorFunction<HttpParams, HttpParams> {
    return super.workspaceParams();
  }

  public coordParams(limit: number = AbstractSearchResultService.DOCUMENT_PAGE_SIZE, offset = 0): OperatorFunction<HttpParams, HttpParams> {
    return super.coordParams(limit, offset);
  }

  public searchItemsParams(pathToCheck: any = null): OperatorFunction<HttpParams, HttpParams> {
    return super.searchItemsParams(pathToCheck);
  }

  public filterItemsParams(path: any = null): OperatorFunction<HttpParams, HttpParams> {
    return super.filterItemsParams(path);
  }

  public updateStoreEntity(entityId: string): void {
    const entitySearchListResult: EsPage<Entity> = !!this.entitySearchResultState ? this.entitySearchResultState.datas : null;
    if (!entitySearchListResult) {
      return;
    }
    const cloneEntitySearchListResult = _.cloneDeep(entitySearchListResult);
    const updatedEntity: Entity = cloneEntitySearchListResult.content.filter((entity: Entity) => entity.cdx_id === entityId)[0];
    if (!!updatedEntity) {
      this.httpClient.get<EntityDetails>(Url.getProtectedApiBaseUrl(this.configAction) + Url.LINKS + this.currentContextService.getCurrentEntityType() + '/' + entityId).subscribe((entityDetails: EntityDetails) => {
        cloneEntitySearchListResult.content.splice(cloneEntitySearchListResult.content.map(entity => entity.cdx_id).indexOf(entityDetails.details.cdx_id), 1, entityDetails.details);
        this.entitySearchResultAction.loadEntitiesSucceeded(cloneEntitySearchListResult);
      });
    }
  }

  public deleteStoreEntity(entityId: string): void {
    const entitySearchListResult: EsPage<Entity> = !!this.entitySearchResultState ? this.entitySearchResultState.datas : null;
    if (!entitySearchListResult) {
      return;
    }
    const cloneEntitySearchListResult = _.cloneDeep(entitySearchListResult);
    const deletedEntity: Entity = cloneEntitySearchListResult.content.filter((entity: Entity) => entity.cdx_id === entityId)[0];
    if (!!deletedEntity) {
      cloneEntitySearchListResult.numberOfElements -= 1;
      cloneEntitySearchListResult.content = cloneEntitySearchListResult.content.filter((entity: Entity) => entity.cdx_id !== deletedEntity.cdx_id);
      this.entitySearchResultAction.loadEntitiesSucceeded(cloneEntitySearchListResult);
    }
  }

  getEntitySearchResultState(): EntitySearchResultState {
    return this.entitySearchResultState;
  }
}
